import React, { useState, useEffect } from "react";
import { loginUser } from "../auth/service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { doLogin } from "../auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Logo from "./Logo";
import "./login-modern.css";
import axios from "../api/baseurl";
import heroImage from "../images/login_hero_doctor_transparent.png";
import SecretKeyDecryption from "./secretKeyDecryption";
import { passwordEncrypt } from "./passwordEncryption";
import { useTheme } from "../context/ThemeContext";

function LabCorporateLogin() {
  const { theme, toggleTheme } = useTheme();
  const { fetchSecretKey } = SecretKeyDecryption();
  const navigate = useNavigate();
  const location = useLocation();
  const [logindata, setLogindata] = useState({
    username: "",
    password: "",
    orgcode: "",
    role: "ROLE_LAB_CORPORATE",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [versionResponse, setVersionResponse] = useState(null);

  const [showOptions, setShowOptions] = useState(() => {
    return localStorage.getItem("soOptions") === "true";
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const soOptionsParam = params.get("soOptions");
    if (soOptionsParam !== null) {
      const isTrue = soOptionsParam === "true";
      localStorage.setItem("soOptions", isTrue ? "true" : "false");
      setShowOptions(isTrue);
    }
  }, [location.search]);

  const toggleShowOptions = () => {
    const nextVal = !showOptions;
    localStorage.setItem("soOptions", nextVal ? "true" : "false");
    setShowOptions(nextVal);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/version');
        console.log(response.data);
        const result = await response.json();
        setVersionResponse(result.version);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (event, field) => {
    const actualValue = event.target.value;
    setLogindata((prevData) => ({ ...prevData, [field]: actualValue }));

    if (field === "username") {
      const email = actualValue;
      if (email && email.includes("@")) {
        fetchOrganizationCode(email);
      }
    }
  };

  const fetchOrganizationCode = async (email) => {
    try {
      const response = await axios.post('/api/accounts/getOrgcodeByEmail', { username: email });
      const orgCode = response.data?.orgcode || response.data?.orgCode || (typeof response.data === 'string' ? response.data : '');

      if (orgCode) {
        setLogindata((prevData) => ({ ...prevData, orgcode: orgCode }));
      }
    } catch (error) {
      console.error('Error fetching organization details by email:', error);
    }
  };


  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const decryptedSecretKey = await fetchSecretKey();
      if (!decryptedSecretKey) {
        setLoading(false);
        return;
      }

      const encryptedPassword = await passwordEncrypt(logindata.password, decryptedSecretKey);
      const encryptedLoginData = {
        ...logindata,
        password: encryptedPassword,
      };

      loginUser(encryptedLoginData)
        .then((data) => {
          const userRole = data.role;

          if (userRole === "ROLE_LAB_CORPORATE") {
            localStorage.setItem("token", data.token);
            doLogin(data, () => {
              navigate("/users/dashboard");
            });
            toast.success("Login success");
          } else {
            toast.error("You do not have the required permissions to log in");
            setLoading(false);
          }
        })
        .catch((error) => {
          console.log(error);
          if (error.response.status === 401) {
            toast.error("Incorrect Email Or Password");
          } else if (error.response.status === 404) {
            const logindetails = { username: logindata.username };
            localStorage.setItem("RegistrationData", JSON.stringify(logindetails));
            toast.error(error.response.data.message);
          } else if (error.response.status === 400) {
            let errorMessage = "Something went wrong on the server!!";
            if (error.response.data && error.response.data.codes) {
              const codes = error.response.data.codes;
              if (codes.includes("IncorrectOrgcode")) {
                errorMessage = error.response.data.defaultMessage;
              } else if (codes.includes("IncorrectPassword")) {
                errorMessage = error.response.data.defaultMessage;
              } else if (codes.includes("EmailNotRegistered")) {
                errorMessage = error.response.data.defaultMessage;
              }
            }
            toast.error(errorMessage);
          } else {
            toast.error("Something went wrong on the server!!");
          }
          setLoading(false);
        });
    } catch (error) {
      console.error("Error during login:", error);
      toast.error("Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="hm-login-page">
      {/* Theme toggle button */}
      <div className="hm-theme-toggle-wrap">
        <button
          type="button"
          className="hm-theme-toggle-btn"
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <i className="material-icons" style={{ fontSize: '16px' }}>
            {theme === "dark" ? "light_mode" : "dark_mode"}
          </i>
          <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="hm-loading-overlay">
          <div className="hm-spinner" />
        </div>
      )}

      <div className="hm-login-container">
        <div className="hm-login-card">

          {/* ── LEFT HERO PANEL ── */}
          <div className="hm-hero-panel">
            <div className="hm-brand">
              <Logo type="full" theme="dark" height="36px" className="hm-brand-logo" />
            </div>

            <div className="hm-hero-text">
              <h2 className="hm-hero-tagline">
                Healthcare,<br /><em>connected</em><br />around you.
              </h2>
              <p className="hm-hero-sub">
                Book tests, consult doctors, manage reports and follow-ups — all in one place.
              </p>
            </div>

            <div className="hm-hero-img-wrap">
              <img src={heroImage} alt="Healthcare professional" className="hm-hero-img" />
            </div>

            <div className="hm-features">
              <div className="hm-feature-item">
                <div className="hm-feature-icon">
                  <i className="material-icons" style={{ fontSize: '16px' }}>security</i>
                </div>
                <div className="hm-feature-text">
                  <strong>Secure &amp; Private</strong>
                  <span>Your data is safe with us</span>
                </div>
              </div>
              <div className="hm-feature-item">
                <div className="hm-feature-icon">
                  <i className="material-icons" style={{ fontSize: '16px' }}>people</i>
                </div>
                <div className="hm-feature-text">
                  <strong>Expert Doctors</strong>
                  <span>Consult with specialists</span>
                </div>
              </div>
              <div className="hm-feature-item">
                <div className="hm-feature-icon">
                  <i className="material-icons" style={{ fontSize: '16px' }}>science</i>
                </div>
                <div className="hm-feature-text">
                  <strong>Advanced Labs</strong>
                  <span>Accurate tests, faster results</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT FORM PANEL ── */}
          <div className="hm-form-panel">
            <div className="hm-form-logo-wrap">
              <div className="hm-form-logo-box">
                <Logo type="icon" height="48px" />
              </div>
            </div>

            <div className="hm-form-title">
              <h1>Welcome <span>Back!</span></h1>
              <p>Login to your HealrMed account</p>
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* Email */}
              <div className="hm-field">
                <div className="hm-field-inner">
                  <div className="hm-field-icon">
                    <i className="material-icons" style={{ fontSize: '18px' }}>email</i>
                  </div>
                  <div className="hm-field-content">
                    <label htmlFor="hm-labcorp-email">E-mail</label>
                    <input
                      id="hm-labcorp-email"
                      type="email"
                      placeholder="Enter your email address"
                      value={logindata.username}
                      onChange={(e) => handleChange(e, "username")}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="hm-field">
                <div className="hm-field-inner">
                  <div className="hm-field-icon">
                    <i className="material-icons" style={{ fontSize: '18px' }}>lock</i>
                  </div>
                  <div className="hm-field-content">
                    <label htmlFor="hm-labcorp-password">Password</label>
                    <input
                      id="hm-labcorp-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={logindata.password}
                      onChange={(e) => handleChange(e, "password")}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="hm-field-eye"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className="material-icons" style={{ fontSize: '18px' }}>
                      {showPassword ? "visibility_off" : "visibility"}
                    </i>
                  </button>
                </div>
              </div>

              {/* Lab Code */}
              <div className="hm-field">
                <div className="hm-field-inner">
                  <div className="hm-field-icon">
                    <i className="material-icons" style={{ fontSize: '18px' }}>domain</i>
                  </div>
                  <div className="hm-field-content">
                    <label htmlFor="hm-labcorp-orgcode">Lab Code</label>
                    <input
                      id="hm-labcorp-orgcode"
                      type="text"
                      placeholder="Enter lab code"
                      value={logindata.orgcode}
                      onChange={(e) => handleChange(e, "orgcode")}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="hm-forgot">
                <Link to="/forgotpassword">Forgot Password?</Link>
              </div>

              <button
                type="submit"
                className="hm-login-btn"
                id="hm-labcorp-login-submit"
                disabled={loading}
              >
                LOGIN
                <i className="material-icons hm-login-btn-arrow" style={{ fontSize: '18px' }}>arrow_forward</i>
              </button>
            </form>

            {showOptions && (
              <>
                <div className="hm-divider">
                  <div className="hm-divider-line" />
                  <span className="hm-divider-text">OR LOGIN AS</span>
                  <div className="hm-divider-line" />
                </div>
                <div className="hm-role-grid">
                  <Link to="/signin" className="hm-role-card">
                    <div className="hm-role-card-icon">
                      <i className="material-icons" style={{ fontSize: '18px' }}>admin_panel_settings</i>
                    </div>
                    <span className="hm-role-card-label">Super Admin</span>
                  </Link>
                  <Link to="/employersignin" className="hm-role-card">
                    <div className="hm-role-card-icon">
                      <i className="material-icons" style={{ fontSize: '18px' }}>business</i>
                    </div>
                    <span className="hm-role-card-label">Employer</span>
                  </Link>
                  <Link to="/labsignin" className="hm-role-card">
                    <div className="hm-role-card-icon">
                      <i className="material-icons" style={{ fontSize: '18px' }}>biotech</i>
                    </div>
                    <span className="hm-role-card-label">Lab</span>
                  </Link>
                  <Link to="/employeesignin" className="hm-role-card">
                    <div className="hm-role-card-icon">
                      <i className="material-icons" style={{ fontSize: '18px' }}>badge</i>
                    </div>
                    <span className="hm-role-card-label">Employee</span>
                  </Link>
                  <Link to="/doctorsignin" className="hm-role-card">
                    <div className="hm-role-card-icon">
                      <i className="material-icons" style={{ fontSize: '18px' }}>medical_services</i>
                    </div>
                    <span className="hm-role-card-label">Doctor</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="hm-login-footer">
        <span>© {new Date().getFullYear()} HealrMed. All rights reserved.</span>
        <div className="hm-footer-sep" />
        <Link to="/privacypolicy">Privacy Policy</Link>
        <div className="hm-footer-sep" />
        <Link to="/termsandcondition">Terms &amp; Conditions</Link>
        <div className="hm-footer-sep" />
        <button
          type="button"
          onClick={toggleShowOptions}
          style={{ background: "none", border: "none", color: "#1aa3d8", font: "inherit", cursor: "pointer", fontWeight: 500 }}
          title="Toggle role login options"
        >
          {showOptions ? "Hide Role Logins" : "Other Role Logins"}
        </button>
      </footer>
    </div>
  );
}

export default LabCorporateLogin;
